import React, { useState, useEffect } from 'react';
import {
	Button,
	Stack,
	Typography,
	TableContainer,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	TextField,
	IconButton,
	Box,
	Paper,
	Divider
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCirclePlus, faTrashAlt, faImage } from '@fortawesome/free-solid-svg-icons';
import Swal from 'sweetalert2';
import moment from 'moment';

import MediumModal from '../../../components/modals/MediumModal';
import PreviewModal from '../../../components/modals/PreviewModal';
import { getToken } from '../../../api/Account';
import { simpleMessage } from '../../../helpers/Helpers';
import { addGastoAsync, anularGastoAsync, getGastosDelDiaByGestorAsync } from '../../../api/Gastos';

const formatter = new Intl.NumberFormat('es-NI', {
	style: 'currency',
	currency: 'NIO'
});

const ESTADOS = {
	1: { label: 'Reportado', color: '#2979ff' },
	2: { label: 'Aplicado', color: '#00a152' },
	3: { label: 'Anulado', color: '#f50057' }
};

const GastosGestorModal = ({ open, onClose, gestorId, sucursalId, fecha, readOnly, onChange }) => {
	const token = getToken();

	const [gastoList, setGastoList] = useState([]);
	const [loading, setLoading] = useState(false);

	const [monto, setMonto] = useState('');
	const [concepto, setConcepto] = useState('');
	const [fotoFile, setFotoFile] = useState(null);
	const [fotoPreview, setFotoPreview] = useState(null);

	const [previewOpen, setPreviewOpen] = useState(false);
	const [selectedImage, setSelectedImage] = useState('');

	useEffect(() => {
		if (open && gestorId) {
			loadGastos();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, gestorId, fecha]);

	const loadGastos = async () => {
		setLoading(true);
		const result = await getGastosDelDiaByGestorAsync(
			token,
			gestorId,
			moment(fecha).format('YYYY-MM-DD')
		);
		setLoading(false);
		if (!result.statusResponse) {
			simpleMessage(result.error, 'error');
			return;
		}
		setGastoList(result.data.result);
	};

	const onChangeFoto = (e) => {
		const file = e.target.files[0];
		if (!file) return;
		if (fotoPreview) URL.revokeObjectURL(fotoPreview);
		setFotoFile(file);
		setFotoPreview(URL.createObjectURL(file));
	};

	const removeFoto = () => {
		if (fotoPreview) URL.revokeObjectURL(fotoPreview);
		setFotoFile(null);
		setFotoPreview(null);
	};

	const resetForm = () => {
		setMonto('');
		setConcepto('');
		removeFoto();
	};

	const addGasto = async () => {
		if (!monto || Number(monto) <= 0) {
			simpleMessage('Debe ingresar un monto mayor que cero', 'warning');
			return;
		}
		if (!concepto.trim()) {
			simpleMessage('Debe ingresar el concepto del gasto', 'warning');
			return;
		}
		if (!fotoFile) {
			simpleMessage('Debe adjuntar una foto del comprobante del gasto', 'warning');
			return;
		}

		const formData = new FormData();
		formData.append('monto', monto);
		formData.append('concepto', concepto.trim().toUpperCase());
		formData.append('sucursalId', sucursalId);
		formData.append('gestorId', gestorId);
		formData.append('foto', fotoFile);

		setLoading(true);
		const result = await addGastoAsync(token, formData);
		setLoading(false);
		if (!result.statusResponse) {
			simpleMessage(result.error, 'error');
			return;
		}

		resetForm();
		simpleMessage('Gasto registrado', 'success');
		await loadGastos();
		onChange && onChange();
	};

	const anularGasto = (id) => {
		Swal.fire({
			title: '¿Estas Seguro?',
			text: 'No podras revertir esto...!',
			icon: 'warning',
			showCancelButton: true,
			confirmButtonColor: '#2979ff',
			cancelButtonColor: '#f50057',
			confirmButtonText: 'Anular!',
			cancelButtonText: 'Cancelar',
			customClass: { popup: 'border' }
		}).then((result) => {
			if (result.isConfirmed) {
				(async () => {
					setLoading(true);
					const res = await anularGastoAsync(token, id);
					setLoading(false);
					if (!res.statusResponse) {
						simpleMessage(res.error, 'error');
						return;
					}
					simpleMessage('Gasto anulado', 'success');
					await loadGastos();
					onChange && onChange();
				})();
			}
		});
	};

	const verFoto = (imageUrl) => {
		setSelectedImage(imageUrl);
		setPreviewOpen(true);
	};

	const total = gastoList
		.filter((g) => g.estadoGasto !== 3)
		.reduce((sum, g) => sum + g.monto, 0);

	return (
		<MediumModal titulo="Gastos del Gestor" isVisible={open} setVisible={onClose}>
			<Stack spacing={2}>
				<TableContainer>
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell>Concepto</TableCell>
								<TableCell align="center">Monto</TableCell>
								<TableCell align="center">Estado</TableCell>
								<TableCell align="center">Acciones</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{gastoList.map((g) => (
								<TableRow key={g.id}>
									<TableCell>{g.concepto}</TableCell>
									<TableCell align="center">{formatter.format(g.monto)}</TableCell>
									<TableCell
										align="center"
										style={{ color: ESTADOS[g.estadoGasto]?.color, fontWeight: 'bold' }}
									>
										{ESTADOS[g.estadoGasto]?.label}
									</TableCell>
									<TableCell align="center">
										{g.foto ? (
											<IconButton
												style={{ color: '#2979ff' }}
												onClick={() => verFoto(g.foto.imageFullPath)}
											>
												<FontAwesomeIcon icon={faImage} />
											</IconButton>
										) : null}
										{!readOnly && g.estadoGasto === 1 ? (
											<IconButton
												style={{ color: '#f50057' }}
												onClick={() => anularGasto(g.id)}
											>
												<FontAwesomeIcon icon={faTrashAlt} />
											</IconButton>
										) : null}
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</TableContainer>

				<Stack direction="row" justifyContent="space-between">
					<Typography fontWeight="bold">Total Gastos (sin anulados)</Typography>
					<Typography fontWeight="bold" color="#f50057">
						{formatter.format(total)}
					</Typography>
				</Stack>

				{!readOnly && (
					<>
						<Divider />
						<Typography fontWeight="bold" color="#00a152">
							Agregar Gasto
						</Typography>

						<Stack direction="row" spacing={2}>
							<TextField
								fullWidth
								size="small"
								label="Monto"
								value={monto}
								onChange={(e) => {
									if (/^\d*\.?\d*$/.test(e.target.value)) setMonto(e.target.value);
								}}
							/>
							<TextField
								fullWidth
								size="small"
								label="Concepto"
								value={concepto}
								onChange={(e) => setConcepto(e.target.value)}
							/>
						</Stack>

						<TextField
							fullWidth
							size="small"
							type="file"
							label="Comprobante del Gasto"
							inputProps={{ accept: 'image/*' }}
							InputLabelProps={{ shrink: true }}
							onChange={onChangeFoto}
						/>

						{fotoPreview && (
							<Box
								sx={{
									position: 'relative',
									p: 1,
									height: 150,
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center'
								}}
								component={Paper}
							>
								<IconButton
									size="small"
									sx={{ position: 'absolute', top: 8, right: 8 }}
									onClick={removeFoto}
								>
									<Close />
								</IconButton>
								<img
									src={fotoPreview}
									alt="Vista previa comprobante"
									style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
								/>
							</Box>
						)}

						<Button
							variant="outlined"
							disabled={loading}
							style={{ color: '#00a152', borderColor: '#00a152' }}
							onClick={addGasto}
						>
							<FontAwesomeIcon style={{ marginRight: 10 }} icon={faCirclePlus} />
							Agregar Gasto
						</Button>
					</>
				)}
			</Stack>

			<PreviewModal
				openModal={previewOpen}
				selectedImage={selectedImage}
				setOpenModal={setPreviewOpen}
				setSelectedImage={setSelectedImage}
			/>
		</MediumModal>
	);
};

export default GastosGestorModal;
